import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import type { WebLegacyWidgetEntity } from '@web/types';

import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { createId } from '@paralleldrive/cuid2';

import { EButtonStyleType } from '@common/enums';
import { ERadioGroupType } from '@ui/sp-radio-group';

import type { ButtonEntity, ButtonItem, ExtendedPositionLegacy, WidgetPositionLegacy } from '@sites/entity-core';
import { createButtonItem } from '@sites/entity-core';
import type { ButtonDefaultForm, ButtonForm, ButtonZeroForm } from '@sites/entity-signal-form';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';

import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { DEFAULT_BUTTON_ICON } from '@web/widgets/common/base/base-button/base-button.const';
import { WebWidgetPositionSettingsComponent } from '@web/shared/components/web-widget-position-settings';
import { WebWidgetHideSettingsComponent } from '@web/shared/components/web-widget-hide-settings';
import { WebWidgetExtendedHideSettingsComponent } from '@web/shared/components/web-widget-extended-hide-settings';
import { SpExtendedPositionSignalSettingsComponent } from '@components/sp-extended-position-signal-settings';
import { SpAnimationSettingsSignalLegacyComponent } from '@components/sp-animation-settings-signal-legacy';

import { ButtonSettingsComponent } from './button-settings/button-settings.component';

import type { OnInit } from '@angular/core';

@Component({
    selector: 'web-button-control',
    templateUrl: './button-control.component.html',
    styleUrls: ['./button-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonElementsModule,
        DragDropModule,
        ButtonSettingsComponent,
        WebWidgetPositionSettingsComponent,
        WebWidgetHideSettingsComponent,
        WebWidgetExtendedHideSettingsComponent,
        SpExtendedPositionSignalSettingsComponent,
        SpAnimationSettingsSignalLegacyComponent,
    ],
})
export class ButtonControlComponent implements OnInit {
    public readonly widget = input.required<WebLegacyWidgetEntity<ButtonEntity['data']>>();
    public readonly isSpecific = input<boolean>(false);
    public readonly isEduSite = input<boolean>(false);

    private readonly translateService: TranslateService = inject(TranslateService);
    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    public readonly ERadioGroupType = ERadioGroupType;

    public readonly EMPTY_LINK_TEXT = this.translateService.instant('provide_link');

    public readonly isDeviceDesktopSig = toSignal(this.layoutFacade.isDeviceDesktop$, { initialValue: true });
    public readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });
    public readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });

    public form!: ButtonForm;

    public readonly editableButtonIndex = signal<number | null>(null);

    public ngOnInit(): void {
        this.destroyRef.onDestroy(() => {
            if (this.form?.inTransaction()) {
                this.form.rollbackTransaction();
            }
        });

        this.form = this.signalFormService.getOrCreateScoped<ButtonForm>(this.widget().id, this.destroyRef);
    }

    /** True when the widget lives inside a zero block — drives template branching. */
    public get isZero(): boolean {
        return this.widget().type === 'zero';
    }

    /** The form narrowed to the default variant — see the video control for the casting rationale. */
    public get defaultForm(): ButtonDefaultForm {
        return this.form as ButtonDefaultForm;
    }

    /** The form narrowed to the zero variant. */
    public get zeroForm(): ButtonZeroForm {
        return this.form as ButtonZeroForm;
    }

    public dropGroup(event: CdkDragDrop<ButtonItem[]>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        // `moveItemInArray` mutates, so reorder a copy and write it back whole.
        const buttons = [...this.form.buttonItems()];
        moveItemInArray(buttons, event.previousIndex, event.currentIndex);
        this.form.setButtons(buttons);
    }

    public addButton(): void {
        this.form.addButton(
            createButtonItem({
                label: this.translateService.instant('control_learn_more'),
                styleType: EButtonStyleType.secondary,
                widthValue: 260,
                elementId: createId(),
                buttonIcon: { ...DEFAULT_BUTTON_ICON },
            })
        );
    }

    public editButton(index: number): void {
        // The settings panel edits the live form; the transaction makes
        // cancel/escape restore the pre-edit state.
        this.form.beginTransaction();
        this.editableButtonIndex.set(index);
    }

    public saveButtonSettings(): void {
        this.form.commitTransaction();
        this.editableButtonIndex.set(null);
    }

    public cancelButtonSettings(): void {
        this.form.rollbackTransaction();
        this.editableButtonIndex.set(null);
    }

    public removeButton(index: number): void {
        if (this.form.buttonItems().length === 1) {
            return;
        }

        this.form.removeButton(index);
    }

    public getButtonSubText(button: ButtonItem): string {
        return button.link.url?.length ? button.link.url : this.EMPTY_LINK_TEXT;
    }

    // --- Group layout ---

    public setHorizontalAlign(value: string): void {
        const align = value as ButtonEntity['data']['horizontalAlign'];

        this.form.transact(() => {
            if (this.form.isSynchronized()) {
                this.form.setMobileHorizontalAlign(align);
                this.form.setTabletHorizontalAlign(align);
            }
            this.form.setHorizontalAlign(align);
        });
    }

    public setSynchronizeStyle(): void {
        const horizontalAlign = this.form.horizontalAlign().value();

        this.form.transact(() => {
            this.form.setMobileHorizontalAlign(horizontalAlign);
            this.form.setTabletHorizontalAlign(horizontalAlign);
            this.form.setIsSynchronizeStyle(!this.form.isSynchronized());
        });
    }

    // --- Layout (default variant) ---

    /**
     * Snapshot for the value/event-based `web-widget-position-settings`
     * component. `computed()` so the binding re-evaluates when the form's
     * position branch changes.
     */
    public readonly defaultPosition = computed<WidgetPositionLegacy | null>(() => {
        const f = this.form as ButtonDefaultForm | undefined;
        if (!f || this.isZero) return null;
        return (f.position().value() as WidgetPositionLegacy) ?? null;
    });

    public onDefaultPositionChange(patch: Partial<WidgetPositionLegacy>): void {
        const current = this.defaultForm.position().value();
        this.defaultForm.setPosition({ ...current, ...patch });
    }

    // --- Layout (zero variant) ---

    /** See the video control's `zeroPosition` — same contract. */
    public readonly zeroPosition = computed<ExtendedPositionLegacy | null>(() => {
        const f = this.form as ButtonZeroForm | undefined;
        if (!f || !this.isZero) return null;
        return f.position().value() as ExtendedPositionLegacy;
    });
}
